import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useOnboarding, ModuleTips } from '../components/onboarding';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Calendar, AlertTriangle, CheckCircle, DollarSign, Clock, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUARTERLY_DEADLINES = {
  1: '04-15',
  2: '06-15',
  3: '09-15',
  4: '01-15'
};

const QuarterlyEstimates = () => {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const { triggerAction } = useOnboarding();
  const [totals, setTotals] = useState({ total_estimated: 0, total_paid: 0, remaining: 0 });
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDeleteEstimateId, setConfirmDeleteEstimateId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [yearSummary, setYearSummary] = useState(null);
  
  const currentYear = new Date().getFullYear();
  const [formData, setFormData] = useState({
    quarter: '1',
    year: currentYear.toString(),
    estimated_income: '',
    estimated_tax: '',
    due_date: '',
    paid: false,
    paid_amount: '0'
  });

  useEffect(() => {
    fetchYears();
    fetchEstimates();
    // Trigger onboarding action when user views quarterly page
    triggerAction('view_quarterly');
  }, []);

  useEffect(() => {
    if (selectedYear) {
      fetchYearSummary();
    }
  }, [selectedYear]);

  const fetchYears = async () => {
    try {
      const response = await axios.get(`${API}/transactions/years`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      const sevenYears = Array.from({ length: 7 }, (_, i) => currentYear - i);
      const allYears = [...new Set([...sevenYears, ...(response.data || [])])].sort((a, b) => b - a);
      setAvailableYears(allYears);
    } catch (error) {
      setAvailableYears(Array.from({ length: 7 }, (_, i) => currentYear - i));
    }
  };

  const fetchYearSummary = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/summary?year=${selectedYear}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setYearSummary(response.data);
      
      // Auto-calculate estimated tax for the form
      // ── Commandment II: use backend-computed fields, no local calculation ──
      const estimatedTax = response.data.estimated_annual_tax ?? 0;
      const suggestedQuarterly = response.data.suggested_quarterly_payment ?? 0;
      setFormData(prev => ({
        ...prev,
        // ── Commandment II: use backend-computed quarterly payment suggestion ──
        estimated_income: Math.round((response.data.net_profit ?? 0) / 4).toString(),  // income form hint (net/4, not a tax calc)
        estimated_tax: suggestedQuarterly.toString(),
        year: selectedYear.toString()
      }));
    } catch (error) {
      // Year summary unavailable
    }
  };

  // calculateQuarterlyTax removed — all tax math is done in the backend (Commandment II)
  // Use response.data.suggested_quarterly_payment and estimated_annual_tax from /reports/schedule-c

  useEffect(() => {
    if (formData.quarter && formData.year) {
      const q = parseInt(formData.quarter);
      const y = parseInt(formData.year);
      const deadline = QUARTERLY_DEADLINES[q];
      const dueYear = q === 4 ? y + 1 : y;
      setFormData(prev => ({ ...prev, due_date: `${dueYear}-${deadline}` }));
    }
  }, [formData.quarter, formData.year]);

  const fetchEstimates = async () => {
    try {
      const response = await axios.get(`${API}/quarterly-estimates`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      // ── Commandment II: use backend-computed totals (no client-side reduce) ──
      const data = response.data;
      setEstimates(Array.isArray(data) ? data : (data.estimates ?? []));
      if (data.totals) setTotals(data.totals);
    } catch (error) {
      toast.error(t('quarterly.failed_load'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/quarterly-estimates`, {
        quarter: parseInt(formData.quarter),
        year: parseInt(formData.year),
        estimated_income: parseFloat(formData.estimated_income),
        estimated_tax: parseFloat(formData.estimated_tax),
        due_date: formData.due_date,
        paid: formData.paid,
        paid_amount: parseFloat(formData.paid_amount) || 0
      }, { headers: getAuthHeaders(), withCredentials: true });
      
      toast.success(t('quarterly.added'));
      setDialogOpen(false);
      setFormData({
        quarter: '1',
        year: currentYear.toString(),
        estimated_income: '',
        estimated_tax: '',
        due_date: '',
        paid: false,
        paid_amount: '0'
      });
      fetchEstimates();
    } catch (error) {
      toast.error(t('quarterly.failed_add'));
    }
  };

  const handleTogglePaid = async (estimate) => {
    try {
      await axios.put(`${API}/quarterly-estimates/${estimate.estimate_id}`, {
        ...estimate,
        paid: !estimate.paid,
        paid_amount: !estimate.paid ? estimate.estimated_tax : 0
      }, { headers: getAuthHeaders(), withCredentials: true });
      
      toast.success(estimate.paid ? t('quarterly.marked_unpaid') : t('quarterly.marked_paid'));
      fetchEstimates();
    } catch (error) {
      toast.error(t('quarterly.failed_update'));
    }
  };

  const handleDelete = async (estimateId) => {
    if (confirmDeleteEstimateId !== estimateId) {
      setConfirmDeleteEstimateId(estimateId);
      toast.info('Click delete again to confirm', { duration: 3000 });
      setTimeout(() => setConfirmDeleteEstimateId(null), 3000);
      return;
    }
    setConfirmDeleteEstimateId(null);
    try {
      await axios.delete(`${API}/quarterly-estimates/${estimateId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success(t('quarterly.deleted'));
      fetchEstimates();
    } catch (error) {
      toast.error(t('quarterly.failed_delete'));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isOverdue = (dueDate, paid) => {
    if (paid) return false;
    return new Date(dueDate) < new Date();
  };

  // ── Commandment II: totals come from backend DTO, not client-side reduce ──
  const totalEstimated = totals.total_estimated;
  const totalPaid      = totals.total_paid;
  const totalRemaining = totals.remaining;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
  <>
    <DashboardLayout>
      <div className="space-y-6" data-testid="quarterly-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-1">{t('quarterly.title')}</h1>
            <p className="text-muted-foreground">{t('quarterly.subtitle')}</p>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-32" data-testid="year-filter-select">
                <SelectValue placeholder={t('quarterly.year')} />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-estimate-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('quarterly.add_estimate')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('quarterly.add_quarterly')}</DialogTitle>
                  {yearSummary && yearSummary.net_income > 0 && (
                    <p className="text-sm text-muted-foreground mt-2">
                      {t('quarterly.based_on_income', { year: selectedYear, amount: formatCurrency(yearSummary.net_income) })}
                    </p>
                  )}
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('quarterly.quarter')}</Label>
                      <Select value={formData.quarter} onValueChange={(v) => setFormData({...formData, quarter: v})}>
                        <SelectTrigger data-testid="quarter-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">{t('quarterly.q1')}</SelectItem>
                          <SelectItem value="2">{t('quarterly.q2')}</SelectItem>
                          <SelectItem value="3">{t('quarterly.q3')}</SelectItem>
                          <SelectItem value="4">{t('quarterly.q4')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('quarterly.year')}</Label>
                      <Select value={formData.year} onValueChange={(v) => setFormData({...formData, year: v})}>
                        <SelectTrigger data-testid="year-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableYears.map(y => (
                            <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('quarterly.estimated_income')}</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={formData.estimated_income}
                      onChange={(e) => setFormData({...formData, estimated_income: e.target.value})}
                      required
                      data-testid="income-input"
                    />
                </div>

                <div className="space-y-2">
                  <Label>{t('quarterly.estimated_tax')}</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={formData.estimated_tax}
                    onChange={(e) => setFormData({...formData, estimated_tax: e.target.value})}
                    required
                    data-testid="tax-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('quarterly.se_tax_tip')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>{t('quarterly.due_date')}</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                    required
                    data-testid="due-date-input"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="paid"
                    checked={formData.paid}
                    onCheckedChange={(checked) => setFormData({...formData, paid: checked})}
                    data-testid="paid-checkbox"
                  />
                  <Label htmlFor="paid" className="cursor-pointer">{t('quarterly.already_paid')}</Label>
                </div>

                <Button type="submit" className="w-full" data-testid="submit-estimate-btn">
                  {t('quarterly.add_estimate')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="shadow-soft border-border/40" data-testid="total-estimated-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('quarterly.total_estimated')}</p>
                <p className="text-2xl font-bold mono">{formatCurrency(totalEstimated)}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-soft border-border/40" data-testid="total-paid-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('quarterly.total_paid')}</p>
                <p className="text-2xl font-bold text-emerald-600 mono">{formatCurrency(totalPaid)}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-soft border-border/40" data-testid="remaining-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('quarterly.remaining')}</p>
                <p className="text-2xl font-bold text-orange-600 mono">{formatCurrency(totalRemaining)}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Estimates List */}
        <Card className="shadow-soft border-border/40" data-testid="estimates-list">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {t('quarterly.your_estimates')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {estimates.length > 0 ? (
              <div className="space-y-4">
                {estimates.map((estimate) => {
                  const overdue = isOverdue(estimate.due_date, estimate.paid);
                  
                  return (
                    <div 
                      key={estimate.estimate_id}
                      className={`p-4 rounded-lg border ${
                        estimate.paid 
                          ? 'bg-emerald-50 border-emerald-200' 
                          : overdue 
                            ? 'bg-red-50 border-red-200' 
                            : 'bg-muted/50 border-border'
                      }`}
                      data-testid={`estimate-${estimate.estimate_id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                            estimate.paid 
                              ? 'bg-emerald-500' 
                              : overdue 
                                ? 'bg-red-500' 
                                : 'bg-primary'
                          }`}>
                            <span className="text-white font-bold">Q{estimate.quarter}</span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">Q{estimate.quarter} {estimate.year}</span>
                              {estimate.paid ? (
                                <Badge className="bg-emerald-500">{t('quarterly.paid')}</Badge>
                              ) : overdue ? (
                                <Badge variant="destructive">{t('quarterly.overdue')}</Badge>
                              ) : (
                                <Badge variant="secondary">{t('quarterly.pending')}</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {t('quarterly.due')}: {formatDate(estimate.due_date)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">{t('quarterly.income')}</p>
                            <p className="font-medium mono">{formatCurrency(estimate.estimated_income)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">{t('quarterly.tax')}</p>
                            <p className="font-bold mono text-lg">{formatCurrency(estimate.estimated_tax)}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant={estimate.paid ? "outline" : "default"}
                              size="sm"
                              onClick={() => handleTogglePaid(estimate)}
                              data-testid={`toggle-paid-${estimate.estimate_id}`}
                            >
                              {estimate.paid ? t('quarterly.undo') : t('quarterly.mark_paid')}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(estimate.estimate_id)}
                              className="text-muted-foreground hover:text-destructive"
                              data-testid={`delete-estimate-${estimate.estimate_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium mb-2">{t('quarterly.no_estimates')}</h3>
                <p className="text-muted-foreground mb-4">
                  {t('quarterly.start_tracking')}
                </p>
                <Button onClick={() => setDialogOpen(true)} data-testid="empty-add-estimate">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('quarterly.add_first')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="shadow-soft border-border/40 bg-blue-50 border-blue-200" data-testid="info-card">
          <CardContent className="p-6">
            <div className="flex gap-4">
              <AlertTriangle className="w-6 h-6 text-blue-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">{t('quarterly.deadlines_title')}</h3>
                <p className="text-sm text-blue-800">
                  {t('quarterly.deadlines_info')}
                </p>
                <p className="text-sm text-blue-700 mt-2">
                  {t('quarterly.requirement_info')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
    <ModuleTips moduleId="quarterly" />
  </>
  );
};

export default QuarterlyEstimates;
